import manifest from './d10FilmManifest.json';
import type { TableDie } from './raceDice.ts';
import type { BaseballDiceLayer } from './baseballDiceFilm.ts';

/**
 * THE ROLLING TEN-SIDED DIE (D-EZ-106) — Howard's pick, option C: aged ivory,
 * black numerals, 6 and 9 dotted, the 10 printed "10".
 *
 * Every all-d10 throw (one d10, or 2d10) plays one of these films through the
 * SAME compositor and tabletop as the Baseball d6s (`createBaseballDiceCompositor`),
 * so the two kinds of die share light, grade, vignette and shadow.
 *
 * Each path is one Bullet rigid-body throw baked in Blender, then rigidly
 * rotated by the trapezohedron's symmetry so each of the ten faces lands on top
 * (`scripts/dice-film/d10_film.py`). The film for a die is chosen by its
 * RECORDED face: `/d10-film/path-NN/face-F.mp4`. Nothing is drawn at random and
 * nothing is resampled — `variation` only picks which throw (and, for two dice,
 * which compatible pair of throws) carries the faces the engine already rolled.
 *
 * Colour: the look is FIXED (an ivory die is ivory whatever its role), so every
 * layer is drawn with the identity tint [1, 1, 1] and stain 0 — the compositor's
 * role tint becomes a no-op and only the common table grade applies, exactly as
 * in the approved stills.
 */
export const D10_FILM_TINT: readonly number[] = [1, 1, 1];
export const D10_FILM_STAIN = 0;

/** One baked throw. `rest` is where the die settles, in 1120x630 film pixels. */
export interface D10FilmPath {
  id: number;
  bounds: readonly number[];
  depth: readonly number[];
  lastMoving: number;
  rest: readonly number[];
  sound: string;
}

export const D10_FILM = manifest as unknown as {
  seconds: number; fps: number; frames: number; sides: number;
  paths: readonly D10FilmPath[];
  layouts: Record<'1' | '2', readonly (readonly number[])[]>;
};

const pad = (path: number) => String(path).padStart(2, '0');
export const d10FilmMovie = (path: number, face: number) => `/d10-film/path-${pad(path)}/face-${face}.mp4`;
export const d10FilmStill = (path: number, face: number) => `/d10-film/path-${pad(path)}/face-${face}.webp`;

/** Layers for one or two ten-sided dice, or null when the throw is anything else. */
export function d10FilmRecipe(dice: readonly TableDie[], variation: number): BaseballDiceLayer[] | null {
  if (dice.length < 1 || dice.length > 2) return null;
  if (dice.some(die => die.sides !== 10 || !Number.isInteger(die.face) || die.face < 1 || die.face > 10)) return null;
  const layouts = D10_FILM.layouts[String(dice.length) as '1' | '2'];
  if (!layouts?.length) return null;
  const n = Math.trunc(Number.isFinite(variation) ? variation : 0);
  const layout = layouts[((n % layouts.length) + layouts.length) % layouts.length];
  return dice.map((die, index) => {
    const path = D10_FILM.paths.find(item => item.id === layout[index])!;
    return {
      die,
      movie: d10FilmMovie(path.id, die.face),
      still: d10FilmStill(path.id, die.face),
      sound: path.sound,
      bounds: path.bounds,
      depth: path.depth,
      tint: D10_FILM_TINT,
      stain: D10_FILM_STAIN,
    };
  });
}
